import { useCallback, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useUiContext } from '../../../../contexts/UiContext/hooks/useUiContext.hook';

import { EmptyFiles } from '../EmptyFiles';
import { DeleteFilesDialog } from '../FilesConsultation/DeleteFilesDialog';
import { FilePreviewDrawer } from '../FilesConsultation/FilePreviewDrawer';
import { MoveFilesDialog } from '../FilesConsultation/MoveFilesDialog';
import { RenameFileDialog } from '../FilesConsultation/RenameFileDialog';
import { useFilesActions } from '../FilesConsultation/hooks/useFilesActions.hook';
import type { FileCategory } from '../FilesConsultation/types/file-category.type';
import type { FileItem } from '../FilesConsultation/types/file-item.type';

import { FileCard } from './FileCard';
import { FilesExplorerToolbar } from './FilesExplorerToolbar';
import type { AccessGate } from '../../../Access/types/access-gate.type';

interface FilesExplorerGridProps {
  readonly allowedFileCategories: readonly FileCategory[];
  readonly files: readonly FileItem[];
  readonly isDeletingFiles?: boolean;
  readonly isLoading: boolean;
  readonly isMovingFiles?: boolean;
  readonly isRenamingFile?: boolean;
  readonly previewFileId?: Id | null;
  readonly selectedFileCategoryId: Id;
  readonly onDeleteFiles: (fileIds: readonly Id[]) => Promise<void>;
  readonly onMoveFiles: (
    params: Readonly<{
      fileIds: readonly Id[];
      destinationFileCategoryId: Id;
    }>,
  ) => Promise<void>;
  readonly onPreviewChange?: (fileId: Id | null) => void;
  readonly onRenameFile: (
    params: Readonly<{ fileId: Id; newOriginalName: string }>,
  ) => Promise<void>;
  readonly onUploadClick: () => void;
  readonly uploadGate?: AccessGate;
}

export const FilesExplorerGrid = ({
  allowedFileCategories,
  files,
  isDeletingFiles = false,
  isLoading,
  isMovingFiles = false,
  isRenamingFile = false,
  previewFileId,
  selectedFileCategoryId,
  onDeleteFiles,
  onMoveFiles,
  onPreviewChange,
  onRenameFile,
  onUploadClick,
  uploadGate,
}: Readonly<FilesExplorerGridProps>) => {
  const [selectedFileIds, setSelectedFileIds] = useState<ReadonlySet<Id>>(
    new Set(),
  );
  const [previousSelectedFileCategoryId, setPreviousSelectedFileCategoryId] =
    useState(selectedFileCategoryId);

  const { showDataGrid, hideDataGrid } = useUiContext();

  // Reuses the DataGrid viewport-bounding mechanism: it's what makes MainContent switch to a
  // fixed-height layout (instead of growing the page) so this grid scrolls internally, same as
  // the content tab's DataGrid does.
  useEffect(() => {
    const token = showDataGrid();

    return () => {
      hideDataGrid(token);
    };
  }, [showDataGrid, hideDataGrid]);

  const {
    closeDeleteDialog,
    closeMoveDialog,
    closePreview,
    closeRenameDialog,
    confirmDelete,
    confirmMove,
    confirmRename,
    deleteError,
    deletePreviewedFile,
    destinationFileCategories,
    fileToRename,
    filesById,
    isDeleteDialogOpen,
    isMoveDialogOpen,
    movePreviewedFile,
    moveError,
    openDeleteDialog,
    openMoveDialog,
    openPreview,
    openRenameDialog,
    pendingDeleteFileIds,
    pendingMoveFileIds,
    previewFile,
    renameError,
    renamePreviewedFile,
    visibleFiles,
  } = useFilesActions({
    allowedFileCategories,
    files,
    onDeleteFiles,
    onMoveFiles,
    onPreviewChange,
    onRenameFile,
    previewFileId,
    selectedFileCategoryId,
  });

  // Reset the row selection when the category changes, computed during render
  // (per https://react.dev/learn/you-might-not-need-an-effect) instead of in an effect,
  // to avoid the extra cascading render an effect-based reset would cause.
  if (selectedFileCategoryId !== previousSelectedFileCategoryId) {
    setPreviousSelectedFileCategoryId(selectedFileCategoryId);
    setSelectedFileIds(new Set());
  }

  const clearSelection = useCallback(() => {
    setSelectedFileIds(new Set());
  }, []);

  const selectAll = useCallback(() => {
    setSelectedFileIds(new Set(visibleFiles.map((file) => file._id)));
  }, [visibleFiles]);

  const toggleSelection = useCallback((fileId: Id) => {
    setSelectedFileIds((previous) => {
      const next = new Set(previous);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const openRowDeleteDialog = useCallback(
    (file: FileItem) => {
      openDeleteDialog([file._id]);
    },
    [openDeleteDialog],
  );

  const openRowMoveDialog = useCallback(
    (file: FileItem) => {
      openMoveDialog([file._id]);
    },
    [openMoveDialog],
  );

  const openBulkDeleteDialog = useCallback(() => {
    openDeleteDialog([...selectedFileIds]);
  }, [openDeleteDialog, selectedFileIds]);

  const openBulkMoveDialog = useCallback(() => {
    openMoveDialog([...selectedFileIds]);
  }, [openMoveDialog, selectedFileIds]);

  const handleConfirmDelete = useCallback(() => {
    confirmDelete(clearSelection);
  }, [confirmDelete, clearSelection]);

  const handleConfirmMove = useCallback(
    (destinationFileCategoryId: Id) => {
      confirmMove(destinationFileCategoryId, clearSelection);
    },
    [confirmMove, clearSelection],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (visibleFiles.length === 0) {
    return <EmptyFiles onUploadClick={onUploadClick} uploadGate={uploadGate} />;
  }

  const canMove = destinationFileCategories.length > 0;

  return (
    <>
      <FilesExplorerToolbar
        onDeleteClick={openBulkDeleteDialog}
        onDeselectAllClick={clearSelection}
        onMoveClick={openBulkMoveDialog}
        onSelectAllClick={selectAll}
        selectedCount={selectedFileIds.size}
      />
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        }}
      >
        {visibleFiles.map((file) => (
          <FileCard
            canMove={canMove}
            file={file}
            isSelected={selectedFileIds.has(file._id)}
            key={file._id}
            onDelete={openRowDeleteDialog}
            onMove={openRowMoveDialog}
            onRename={openRenameDialog}
            onToggleSelect={toggleSelection}
            onView={openPreview}
          />
        ))}
      </Box>
      <DeleteFilesDialog
        count={pendingDeleteFileIds.length}
        error={deleteError}
        isLoading={isDeletingFiles}
        isOpen={isDeleteDialogOpen}
        onCancel={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
      <MoveFilesDialog
        categories={destinationFileCategories}
        count={pendingMoveFileIds.length}
        error={moveError}
        isLoading={isMovingFiles}
        isOpen={isMoveDialogOpen}
        onCancel={closeMoveDialog}
        onConfirm={handleConfirmMove}
      />
      <RenameFileDialog
        error={renameError}
        isLoading={isRenamingFile}
        isOpen={!!fileToRename}
        onCancel={closeRenameDialog}
        onConfirm={confirmRename}
        originalName={fileToRename?.originalName ?? EMPTY_STRING}
      />
      <FilePreviewDrawer
        allowedFileCategories={allowedFileCategories}
        file={previewFile}
        files={visibleFiles}
        filesById={filesById}
        isDeletingFile={isDeletingFiles}
        isMovingFile={isMovingFiles}
        isRenamingFile={isRenamingFile}
        onClose={closePreview}
        onDeleteFile={deletePreviewedFile}
        onMoveFile={movePreviewedFile}
        onNavigate={openPreview}
        onRenameFile={renamePreviewedFile}
        selectedFileCategoryId={selectedFileCategoryId}
      />
    </>
  );
};
